import React, {useState} from "../_snowpack/pkg/react.js";
import {Link} from "../_snowpack/pkg/react-router-dom.js";
import {Trans, useTranslation} from "../_snowpack/pkg/react-i18next.js";
import {
  AlertVariant,
  Button,
  ButtonVariant,
  Label,
  PageSection,
  Popover,
  Tab,
  TabTitleText,
  ToolbarItem
} from "../_snowpack/pkg/@patternfly/react-core.js";
import {CheckCircleIcon} from "../_snowpack/pkg/@patternfly/react-icons.js";
import {useAdminClient} from "../context/auth/AdminClient.js";
import {KeycloakDataTable} from "../components/table-toolbar/KeycloakDataTable.js";
import {KeycloakTabs} from "../components/keycloak-tabs/KeycloakTabs.js";
import {ListEmptyState} from "../components/list-empty-state/ListEmptyState.js";
import {ViewHeader} from "../components/view-header/ViewHeader.js";
import {useRealm} from "../context/realm-context/RealmContext.js";
import {useConfirmDialog} from "../components/confirm-dialog/ConfirmDialog.js";
import {useAlerts} from "../components/alert/Alerts.js";
import {toUpperCase} from "../util.js";
import useToggle from "../utils/useToggle.js";
import {DuplicateFlowModal} from "./DuplicateFlowModal.js";
import {toCreateFlow} from "./routes/CreateFlow.js";
import {toFlow} from "./routes/Flow.js";
import {RequiredActions} from "./RequiredActions.js";
import {Policies} from "./policies/Policies.js";
import "./authentication-section.css";
import helpUrls from "../help-urls.js";
const realmFlows = [
  "browserFlow",
  "registrationFlow",
  "directGrantFlow",
  "resetCredentialsFlow",
  "clientAuthenticationFlow",
  "dockerAuthenticationFlow"
];
export default function AuthenticationSection() {
  const {t} = useTranslation("authentication");
  const adminClient = useAdminClient();
  const {realm} = useRealm();
  const [key, setKey] = useState(0);
  const refresh = () => setKey(new Date().getTime());
  const {addAlert, addError} = useAlerts();
  const [selectedFlow, setSelectedFlow] = useState();
  const [open, toggleOpen, setOpen] = useToggle();
  const loader = async () => {
    const clients = await adminClient.clients.find();
    const idps = await adminClient.identityProviders.find();
    const realmRep = await adminClient.realms.findOne({realm});
    if (!realmRep) {
      throw new Error(t("common:notFound"));
    }
    const defaultFlows = Object.entries(realmRep).filter((entry) => realmFlows.includes(entry[0])).map((entry) => entry[1]);
    const flows = await adminClient.authenticationManagement.getFlows();
    for (const flow of flows) {
      flow.usedBy = {values: []};
      const client = clients.find((client2) => client2.authenticationFlowBindingOverrides && (client2.authenticationFlowBindingOverrides["direct_grant"] === flow.id || client2.authenticationFlowBindingOverrides["browser"] === flow.id));
      if (client) {
        flow.usedBy.type = "specificClients";
        flow.usedBy.values.push(client.clientId);
      }
      const idp = idps.find((idp2) => idp2.firstBrokerLoginFlowAlias === flow.alias || idp2.postBrokerLoginFlowAlias === flow.alias);
      if (idp) {
        flow.usedBy.type = "specificProviders";
        flow.usedBy.values.push(idp.alias);
      }
      const isDefault = defaultFlows.includes(flow.alias);
      if (isDefault) {
        flow.usedBy.type = "default";
      }
    }
    return flows;
  };
  const [toggleDeleteDialog, DeleteConfirm] = useConfirmDialog({
    titleKey: "authentication:deleteConfirmFlow",
    children: /* @__PURE__ */ React.createElement(Trans, {
      i18nKey: "authentication:deleteConfirmFlowMessage"
    }, " ", /* @__PURE__ */ React.createElement("strong", null, {flow: selectedFlow ? selectedFlow.alias : ""}), "."),
    continueButtonLabel: "common:delete",
    continueButtonVariant: ButtonVariant.danger,
    onConfirm: async () => {
      try {
        await adminClient.authenticationManagement.deleteFlow({
          flowId: selectedFlow.id
        });
        refresh();
        addAlert(t("deleteFlowSuccess"), AlertVariant.success);
      } catch (error) {
        addError("authentication:deleteFlowError", error);
      }
    }
  });
  const UsedBy = ({id, usedBy: {type, values}}) => /* @__PURE__ */ React.createElement(React.Fragment, null, (type === "specificProviders" || type === "specificClients") && /* @__PURE__ */ React.createElement(Popover, {
    key: id,
    "aria-label": t("usedBy"),
    bodyContent: /* @__PURE__ */ React.createElement("div", {
      key: `usedBy-${id}-${values}`
    }, t("appliedBy" + (type === "specificClients" ? "Clients" : "Providers")), " ", values.map((used, index) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("strong", null, used), index < values.length - 1 ? ", " : "")))
  }, /* @__PURE__ */ React.createElement(Button, {
    variant: ButtonVariant.link,
    key: `button-${id}`
  }, /* @__PURE__ */ React.createElement(CheckCircleIcon, {
    className: "keycloak_authentication-section__usedby",
    key: `icon-${id}`
  }), " ", t(type))), type === "default" && /* @__PURE__ */ React.createElement(Button, {
    key: id,
    variant: ButtonVariant.link,
    isDisabled: true
  }, /* @__PURE__ */ React.createElement(CheckCircleIcon, {
    className: "keycloak_authentication-section__usedby",
    key: `icon-${id}`
  }), " ", t("default")), !type && /* @__PURE__ */ React.createElement(Button, {
    key: id,
    variant: ButtonVariant.link,
    isDisabled: true
  }, t("notInUse")));
  const AliasRenderer = ({
    id,
    alias,
    usedBy,
    builtIn
  }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Link, {
    to: toFlow({
      realm,
      id,
      usedBy: usedBy.type || "notInUse",
      builtIn: builtIn ? "builtIn" : void 0
    }),
    key: `link-${id}`
  }, toUpperCase(alias)), " ", builtIn && /* @__PURE__ */ React.createElement(Label, {
    key: `label-${id}`
  }, t("buildIn")));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(DeleteConfirm, null), open && /* @__PURE__ */ React.createElement(DuplicateFlowModal, {
    name: selectedFlow ? selectedFlow.alias : "",
    description: selectedFlow?.description,
    toggleDialog: toggleOpen,
    onComplete: () => {
      refresh();
      setOpen(false);
    }
  }), /* @__PURE__ */ React.createElement(ViewHeader, {
    titleKey: "authentication:title",
    subKey: "authentication:authenticationExplain",
    helpUrl: helpUrls.authenticationUrl,
    divider: false
  }), /* @__PURE__ */ React.createElement(PageSection, {
    variant: "light",
    className: "pf-u-p-0"
  }, /* @__PURE__ */ React.createElement(KeycloakTabs, {
    isBox: true
  }, /* @__PURE__ */ React.createElement(Tab, {
    eventKey: "flows",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("flows"))
  }, /* @__PURE__ */ React.createElement(KeycloakDataTable, {
    key,
    loader,
    ariaLabelKey: "authentication:title",
    searchPlaceholderKey: "authentication:searchForFlow",
    toolbarItem: /* @__PURE__ */ React.createElement(ToolbarItem, null, /* @__PURE__ */ React.createElement(Button, {
      component: (props) => /* @__PURE__ */ React.createElement(Link, {
        ...props,
        to: toCreateFlow({realm})
      })
    }, t("createFlow"))),
    actionResolver: ({data}) => {
      const defaultActions = [
        {
          title: t("duplicate"),
          onClick: () => {
            setOpen(true);
            setSelectedFlow(data);
          }
        }
      ];
      if (data.builtIn || data.usedBy.values.length > 0) {
        return defaultActions;
      } else {
        return [
          {
            title: t("common:delete"),
            onClick: () => {
              setSelectedFlow(data);
              toggleDeleteDialog();
            }
          },
          ...defaultActions
        ];
      }
    },
    columns: [
      {
        name: "alias",
        displayKey: "authentication:flowName",
        cellRenderer: AliasRenderer
      },
      {
        name: "usedBy",
        displayKey: "authentication:usedBy",
        cellRenderer: UsedBy
      },
      {
        name: "description",
        displayKey: "common:description"
      }
    ],
    emptyState: /* @__PURE__ */ React.createElement(ListEmptyState, {
      message: t("emptyEvents"),
      instructions: t("emptyEventsInstructions")
    })
  })), /* @__PURE__ */ React.createElement(Tab, {
    id: "requiredActions",
    eventKey: "requiredActions",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("requiredActions"))
  }, /* @__PURE__ */ React.createElement(RequiredActions, null)), /* @__PURE__ */ React.createElement(Tab, {
    id: "policies",
    eventKey: "policies",
    title: /* @__PURE__ */ React.createElement(TabTitleText, null, t("policies"))
  }, /* @__PURE__ */ React.createElement(Policies, null)))));
}
